"use client"
import { parseAsArrayOf, parseAsStringLiteral, useQueryState } from "nuqs"
import { QUERY_PARAM_KEYS } from "./keys"

// List of all available swap protocols and the "all" option
const SWAP_PROTOCOLS = [
  "all",
  "opensea",
  "uniswap",
  "orca",
  "raydium",
  "pancake_swap",
  "aerodrome",
  "pumpfun",
  "meteora",
  "solfi",
] as const

export type RelayersQueryParam = (typeof SWAP_PROTOCOLS)[number]

const parser = parseAsStringLiteral(SWAP_PROTOCOLS)

export function useRelayersQueryParam({
  shallow = true,
}: {
  shallow?: boolean
} = {}) {
  // Default to ["opensea"] when no value is set
  const [values, setValues] = useQueryState(
    QUERY_PARAM_KEYS.relayers,
    parseAsArrayOf(parser).withOptions({ shallow }).withDefault(["opensea"]),
  )

  async function onSelectRelayer(relayer: RelayersQueryParam) {
    // Toggle selection logic for multiple selection:
    // - If relayer is already selected, remove it
    // - If relayer is not selected, add it
    // - Special handling for "all": selecting "all" shows all protocols
    let newValues: RelayersQueryParam[]

    if (relayer === "all") {
      // If "all" is clicked, toggle between all and opensea only
      newValues = values.includes("all") ? ["opensea"] : ["all"]
    } else if (values.includes("all")) {
      // If "all" is currently selected and we're selecting a specific one
      newValues = [relayer]
    } else if (values.includes(relayer)) {
      // Remove the relayer if it's already selected
      newValues = values.filter(r => r !== relayer)
      // If no relayers left, show all
      if (newValues.length === 0) {
        newValues = ["all"]
      }
    } else {
      // Add the relayer if it's not selected
      newValues = [...values.filter(r => r !== "all"), relayer]
    }

    await setValues(newValues)
  }

  async function setRelayers(relayers: RelayersQueryParam[] | null) {
    await setValues(relayers && relayers.length > 0 ? relayers : ["opensea"])
  }

  async function clearRelayers() {
    // Clear should show all relayers
    await setValues(["all"])
  }

  // Convert to format expected by GraphQL
  // If "all" is selected, return empty array (no filter = show all)
  // Otherwise return the selected protocols
  const activeRelayers = values.includes("all") ? [] : values

  return {
    relayer: values[0] ?? null,
    relayers: values,
    activeRelayers,
    onSelectRelayer,
    setRelayers,
    clearRelayers,
    hasRelayerFilter: !values.includes("all"),
  }
}
